/**
 * Zod schemas for the /now page data contracts.
 *
 * These schemas mirror the Pydantic response models from fleet-api Plan 03-01.
 * They provide runtime validation of API responses and infer static TypeScript types.
 *
 * Data sources:
 *   GET /v1/sites                       -> SiteSummary[]
 *   GET /v1/sites/{id}/live-state       -> LiveState
 *   GET /v1/insights/deterrence         -> DeterrenceKPIs
 *   GET /v1/alerts?site_id&since=PT2H   -> AlertSummary[]
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// DeterrenceKPIs
// ---------------------------------------------------------------------------

/**
 * KPIs powering the deterrence hero strip.
 *
 * Returned by GET /v1/insights/deterrence?site_id=<uuid>&window=<week|month|all>
 */
export const DeterrenceKPIsSchema = z.object({
  /** Number of phones turned away in the requested window. */
  turned_away: z.number().int(),
  /** Total approach events (turned_away + entered). */
  total_approaches: z.number().int(),
  /** Percent change vs the previous equivalent window (+N.N = up, -N.N = down). */
  delta_pct_vs_prev_window: z.number(),
  /** Deterrence rate as a fraction [0, 1]. */
  deterrence_rate: z.number().min(0).max(1),
  /** Coverage uptime as a fraction [0, 1]. */
  coverage_uptime_pct: z.number().min(0).max(1),
  /** Number of sensors currently active. */
  currently_active: z.number().int(),
});

export type DeterrenceKPIs = z.infer<typeof DeterrenceKPIsSchema>;

// ---------------------------------------------------------------------------
// Deterrence breakdown (Plan 12-10 endpoint, Plan 12-11 drawer consumer)
// ---------------------------------------------------------------------------

/**
 * One row in the Performance drill-in drawer (Plan 12-11).
 *
 * Returned by GET /v1/insights/deterrence/breakdown. Mirrors the
 * fleet-api Pydantic DeterrenceBreakdownItem schema.
 *
 * The `id` field is the cloud-side PK of the entry_events_mirror row;
 * the drawer uses it to navigate to /event/entry-<id> on click.
 */
export const DeterrenceBreakdownItemSchema = z.object({
  /** Cloud-side PK; navigation target. */
  id: z.number().int(),
  /** ISO 8601 event timestamp. */
  ts: z.string(),
  /** Sensor chip id; null when the detector could not identify a device. */
  chip_id: z.string().nullable(),
  /** Optional fingerprint string (server may omit on legacy paths). */
  fingerprint: z.string().nullable().optional(),
  /** Zone numeric id; null if site-level. */
  zone_id: z.number().int().nullable(),
  /** Zone display name; null if unknown. */
  zone_name: z.string().nullable().optional(),
  /** RSSI in dBm. */
  rssi: z.number().int().nullable(),
  /** Computed outcome: never null on the wire (server fills 'unknown'). */
  deterrence_outcome: z.enum(['deterred', 'escalated', 'unknown']),
});
export type DeterrenceBreakdownItem = z.infer<typeof DeterrenceBreakdownItemSchema>;

/**
 * Period filter for the breakdown endpoint (mirrors DeterrenceWindow).
 *
 * `all` skips the time cutoff and paginates the full history of an
 * org's entry events for the site.
 */
export const DeterrenceBreakdownPeriodSchema = z.enum(['week', 'month', 'all']);
export type DeterrenceBreakdownPeriod = z.infer<typeof DeterrenceBreakdownPeriodSchema>;

/**
 * Outcome filter for the breakdown endpoint.
 *
 * `all` disables the filter; the other three values map 1:1 to the
 * `deterrence_outcome` column / on-the-fly derivation.
 */
export const DeterrenceBreakdownOutcomeFilterSchema = z.enum([
  'all',
  'deterred',
  'escalated',
  'unknown',
]);
export type DeterrenceBreakdownOutcomeFilter = z.infer<
  typeof DeterrenceBreakdownOutcomeFilterSchema
>;

/**
 * Paginated response envelope for the breakdown endpoint.
 *
 * The drawer reads `items` for the row list and `total` + `limit` +
 * `offset` for the pager; `site_id` + `period` + `outcome` echo the
 * request so the consumer can verify it got what it asked for.
 */
export const DeterrenceBreakdownResponseSchema = z.object({
  items: z.array(DeterrenceBreakdownItemSchema),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  site_id: z.string().uuid(),
  period: DeterrenceBreakdownPeriodSchema,
  outcome: DeterrenceBreakdownOutcomeFilterSchema,
});
export type DeterrenceBreakdownResponse = z.infer<
  typeof DeterrenceBreakdownResponseSchema
>;

// ---------------------------------------------------------------------------
// LiveState
// ---------------------------------------------------------------------------

/**
 * Zone snapshot: one of the four concentric security tiers.
 *
 * Phase 3 always returns exactly 4 zones; the viz renders them directly.
 */
export const ZoneSnapshotSchema = z.object({
  /** Zone identifier (maps to concentric ring position). */
  id: z.enum(['perimeter', 'threshold', 'interior', 'core']),
  /** Human-readable zone name. */
  name: z.string(),
  /** Tier number: 1 = perimeter (outer), 4 = core (center). */
  tier: z.number().int().min(1).max(4),
});

export type ZoneSnapshot = z.infer<typeof ZoneSnapshotSchema>;
export type ZoneId = ZoneSnapshot['id'];

/**
 * Sensor snapshot: current health and telemetry for one physical sensor node.
 */
export const SensorSnapshotSchema = z.object({
  /** Hardware chip ID (unique device identifier). */
  chip_id: z.string(),
  /** Operator-assigned friendly name; null if not configured. */
  friendly_name: z.string().nullable(),
  /** Health status derived from heartbeat recency. */
  health: z.enum(['healthy', 'stale', 'offline']),
  /** ISO 8601 timestamp of last heartbeat; null if never received. */
  last_heartbeat_at: z.string().datetime().nullable(),
  /** Packets per second from last heartbeat. */
  packet_rate: z.number(),
});

export type SensorSnapshot = z.infer<typeof SensorSnapshotSchema>;

/**
 * Live state response: initial snapshot for concentric viz on page load.
 *
 * Subsequent updates arrive via WebSocket (Plan 03-06).
 */
export const LiveStateSchema = z.object({
  zones: z.array(ZoneSnapshotSchema),
  sensors: z.array(SensorSnapshotSchema),
});

export type LiveState = z.infer<typeof LiveStateSchema>;

// ---------------------------------------------------------------------------
// AlertSummary
// ---------------------------------------------------------------------------

/**
 * Alert outcome: the resolved outcome of a detection event.
 */
export const AlertOutcomeSchema = z.enum(['turned_away', 'entered', 'loitering', 'other']);
export type AlertOutcome = z.infer<typeof AlertOutcomeSchema>;

/**
 * Alert summary: a single past alert for the event ribbon.
 *
 * Returned by GET /v1/alerts?site_id=<uuid>&since=PT2H&limit=200
 */
export const AlertSummarySchema = z.object({
  /** Alert primary key. */
  id: z.number().int(),
  /** Owning site UUID (org-scoped). */
  site_id: z.string().uuid(),
  /**
   * Zone numeric ID; null if alert is site-level.
   *
   * Plan 15-07 (UNI-13, 2026-05-20): now .optional() because the cloud
   * fleet-api AlertSummary payload does NOT include this field, while the
   * on-site signalsentry-frontend payload does. The shared schema accepts
   * the union of both wire shapes; the SPA renders null when absent.
   */
  zone_id: z.number().int().nullable().optional().default(null),
  /** Zone display name; null if zone deleted or site-level. */
  zone_name: z.string().nullable().optional().default(null),
  /**
   * Device fingerprint hash; null if unknown.
   *
   * Plan 15-07 (UNI-13): .optional() — cloud fleet-api AlertSummary omits
   * this field. On-site payload still ships it.
   */
  fingerprint: z.string().nullable().optional().default(null),
  /** Raw alert type string from detector. */
  alert_type: z.string(),
  /**
   * Severity classification. The canonical wire enum is
   * 'info' | 'warning' | 'critical'.
   *
   * Plan 15-07 (UNI-13, 2026-05-20): the previous z.enum(...) declaration
   * was fragile against any future cloud-mirror sync that bypassed the
   * alerts_mirror CHECK constraint. We now accept any string (and null)
   * at parse-time and coerce unknowns to 'info' via .transform so the
   * page is resilient to drift. The backend Plan 15-07 fix also coalesces
   * at the response boundary; this transform is the belt-and-suspenders
   * contract lock for defense-in-depth.
   */
  severity: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v): 'info' | 'warning' | 'critical' => {
      if (v === 'info' || v === 'warning' || v === 'critical') return v;
      return 'info';
    }),
  /** ISO 8601 timestamp when alert was opened. */
  ts: z.string().datetime(),
  /**
   * ISO 8601 timestamp when alert was resolved; null if still active.
   *
   * Plan 15-07 (UNI-13): .optional() — cloud fleet-api AlertSummary omits.
   */
  resolved_at: z.string().datetime().nullable().optional().default(null),
  /**
   * Resolved outcome; null if unresolved or ambiguous.
   *
   * Plan 15-07 (UNI-13): .optional() — cloud fleet-api AlertSummary omits.
   */
  outcome: AlertOutcomeSchema.nullable().optional().default(null),
  /**
   * Whether a camera clip is available for this event.
   *
   * Plan 15-07 (UNI-13): .optional() with default false — cloud fleet-api
   * AlertSummary omits this field; the cross-site admin inbox renders
   * false (no camera clip) for cloud-mirrored rows until the camera
   * pipeline lands on the cloud side.
   */
  has_camera_clip: z.boolean().optional().default(false),
  /**
   * Presigned thumbnail URL; null if no clip or clip not yet rendered.
   *
   * Plan 15-07 (UNI-13): .optional() — cloud fleet-api AlertSummary omits.
   */
  thumbnail_url: z.string().url().nullable().optional().default(null),
});

export type AlertSummary = z.infer<typeof AlertSummarySchema>;

// ---------------------------------------------------------------------------
// SiteSummary
// ---------------------------------------------------------------------------

/**
 * Site summary: org-scoped site metadata for site selection.
 *
 * Returned by GET /v1/sites (list) and GET /v1/sites/{id} (detail).
 *
 * 2026-04-27 amendment: sensor_count_online and sensor_count_total are
 * optional with default 0 because the fleet-api Plan 04-01 site list
 * endpoint does not currently aggregate sensor counts (sensors live in
 * the per-site signalsentry-api stack accessed via the API-08 reverse
 * proxy, not in fleet's own database). Until fleet-api derives counts
 * from cloud-mirrored heartbeats or proxies a count query upstream, the
 * values default to 0 and the SitesTable renders "0/0" for the cell.
 * This avoids a Zod validation crash when the wire shape lacks the field.
 */
export const SiteSummarySchema = z.object({
  /** Site UUID. */
  site_id: z.string().uuid(),
  /** URL-safe slug for display / routing. */
  slug: z.string(),
  /** Site display name. */
  name: z.string(),
  /** Operational status of the site. */
  status: z.enum(['pending', 'online', 'degraded', 'offline', 'decommissioned']),
  /** ISO 8601 timestamp of last sensor heartbeat; null if never connected. */
  last_seen: z.string().datetime().nullable(),
  /** Sensors currently reporting heartbeats. Optional; defaults to 0. */
  sensor_count_online: z.number().int().optional().default(0),
  /** Total sensors registered to this site. Optional; defaults to 0. */
  sensor_count_total: z.number().int().optional().default(0),
  /**
   * Phase 11.1 follow-up 2026-05-11: ISO 8601 timestamp of the most
   * recent alert mirrored for this site, or null when zero alerts exist.
   * Derived server-side via `MAX(alerts_mirror.ts)` aggregated against
   * `alerts_mirror` and surfaced on BOTH GET /v1/sites and GET
   * /v1/sites/{id} (parity fix shipped in fleet-api 9b699e6). Added to
   * the base SummarySchema so SiteDetailSchema + SiteDetailAdminSchema
   * pick it up via `.extend()` without each having to re-declare.
   */
  last_alert_ts: z.string().datetime().nullable().optional(),
});

export type SiteSummary = z.infer<typeof SiteSummarySchema>;

// ---------------------------------------------------------------------------
// SiteDetail (Plan 03-07: FrozenMapSnapshot dependency)
// ---------------------------------------------------------------------------

/**
 * Geographic location attached to a site.
 *
 * Used by FrozenMapSnapshot to center the MapLibre map on the site.
 * The fleet-api SiteDetail response includes this field from Phase 3 Plan 03-01.
 *
 * Phase 11.1 amendment 2026-05-11: `address` (and arbitrary extra hint
 * fields like `entry_exit_indicated`, `source_figure`) ride along on the
 * same JSONB. Marked optional + `passthrough` so any future server-side
 * additions to the bag flow through without a schema bump.
 */
export const LocationSchema = z
  .object({
    lat: z.number(),
    lng: z.number(),
    address: z.string().optional(),
  })
  .passthrough();

export type Location = z.infer<typeof LocationSchema>;

/**
 * Full site detail returned by GET /v1/sites/{site_id}.
 *
 * Extends SiteSummarySchema with additional operational fields and the
 * geographic location required by FrozenMapSnapshot (Plan 03-07).
 *
 * Required export: FrozenMapSnapshot imports useSiteDetail which returns
 * SiteDetail. This schema MUST exist regardless of whether SiteSummary
 * is sufficient elsewhere.
 */
/**
 * Phase 10.5 v1.1 heartbeat payload, stored verbatim as
 * `sites.last_heartbeat` JSONB. Cloud SPA reads this to render
 * the live-state block on Site Detail without hitting the
 * per-site Tailscale proxy. All fields are optional because:
 * - v1.0 callers (pre-Phase-10.5) populated only `{ts, services}`
 * - v1.1 callers populate the rich blocks but env_readings is only
 *   present when the on-site healthstatus service is wired
 * - The schema uses passthrough so new top-level keys server-side
 *   flow through without an SPA bump.
 */
export const HeartbeatPayloadSchema = z
  .object({
    schema_version: z.string().optional(),
    ts: z.string().optional(),
    sensors: z
      .object({
        online: z.number().optional(),
        stale: z.number().optional(),
        offline: z.number().optional(),
        total: z.number().optional(),
        registry: z.array(z.record(z.unknown())).optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    cameras: z
      .object({
        online: z.number().optional(),
        offline: z.number().optional(),
        total: z.number().optional(),
        list: z.array(z.record(z.unknown())).optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    alerts_24h: z
      .object({
        total: z.number().optional(),
        info: z.number().optional(),
        warning: z.number().optional(),
        critical: z.number().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    entry_events_24h: z
      .object({ total: z.number().optional() })
      .passthrough()
      .nullable()
      .optional(),
    system: z
      .object({
        free_memory_gb: z.number().optional(),
        cpu_percent_5m_avg: z.number().optional(),
        disk_free_gb: z.number().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    tailscale: z
      .object({
        connected: z.boolean().optional(),
        last_handshake_ts: z.string().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    env_readings: z
      .object({
        latest: z
          .object({
            temperature_c: z.number().optional(),
            humidity_pct: z.number().optional(),
            pressure_hpa: z.number().optional(),
            ts: z.string().optional(),
          })
          .passthrough()
          .nullable()
          .optional(),
        thresholds: z.record(z.unknown()).nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    services: z.record(z.string()).nullable().optional(),
  })
  .passthrough();

export type HeartbeatPayload = z.infer<typeof HeartbeatPayloadSchema>;

/**
 * Phase 12 Plan 12-02: Slim denormalized location object embedded in the
 * SiteDetail response. Source of truth lives in the `locations` table on the
 * fleet-api side; this schema mirrors the Pydantic LocationDenormResponse
 * shape (`signalsentry-fleet-api/fleet_api/locations/schemas.py`). Plan 12-08
 * (Sentry Detail header) consumes this for the location + coords lines.
 */
export const LocationDenormSchema = z.object({
  /** UUID of the location row in fleet Postgres. */
  id: z.string().uuid(),
  /** Display name (e.g. "Pantex Building 12-145"). */
  name: z.string(),
  /** Short disambiguator (e.g. "12-145"); null when not set. */
  building_code: z.string().nullable(),
  /** WGS84 latitude in degrees, coerced to float at the wire boundary. */
  lat: z.number(),
  /** WGS84 longitude in degrees, coerced to float at the wire boundary. */
  lng: z.number(),
  /** Optional street-level address. */
  address: z.string().nullable(),
});
export type LocationDenorm = z.infer<typeof LocationDenormSchema>;

export const SiteDetailSchema = SiteSummarySchema.extend({
  /**
   * Geographic coordinates of the site, or null if not configured.
   * FrozenMapSnapshot centers the MapLibre map on these coordinates.
   */
  location: LocationSchema.nullable().optional(),
  /** IANA timezone identifier for the site (e.g. 'America/Los_Angeles'). */
  timezone: z.string().optional(),
  /** Tailscale MagicDNS hostname for the on-site Pi. */
  tailscale_hostname: z.string().nullable().optional(),
  /** Fleet-api reverse-proxy URL to the site local API. */
  local_api_url: z.string().nullable().optional(),
  /** Freeform operator notes; null if unset. */
  notes: z.string().nullable().optional(),
  /** ISO 8601 row creation timestamp. */
  created_at: z.string().datetime().optional(),
  /** ISO 8601 row last-modified timestamp. */
  updated_at: z.string().datetime().optional(),
  /** Parent organization UUID. */
  org_id: z.string().uuid().optional(),
  /**
   * Phase 10.5 v1.1: full heartbeat payload bag stored as JSONB on
   * the server. Populated by the on-site heartbeat_poller; null
   * until the first heartbeat lands. Cloud SPA reads this to render
   * sensors / cameras / env_readings tiles without proxying.
   */
  last_heartbeat: HeartbeatPayloadSchema.nullable().optional(),
  /**
   * Phase 12 Plan 12-02: FK to the `locations` table. NULL when the sentry
   * has not yet been assigned to a location. Optional because legacy seed
   * data may not include the field.
   */
  location_id: z.string().uuid().nullable().optional(),
  /**
   * Phase 12 Plan 12-02: denormalized snapshot of the assigned location
   * (id + name + building_code + lat + lng + address). Populated server-side
   * via a JOIN; null when location_id is null. Plan 12-08 reads this for the
   * Sentry Detail header metadata strip without a second API call.
   */
  location_obj: LocationDenormSchema.nullable().optional(),
});

export type SiteDetail = z.infer<typeof SiteDetailSchema>;
